import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./forgotPassword.css";
import { Mail } from "lucide-react";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import register_mark from "../../assets/register_mark.png";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const navigate = useNavigate();

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
  }, []);

  const normalizeEmail = (email) => {
    if (!email) return "";
    return email.trim().toLowerCase();
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const normalizedEmail = normalizeEmail(email);
      
      if (!normalizedEmail) {
        setError("Email is required");
        setLoading(false);
        return;
      }

      if (!isValidEmail(normalizedEmail)) {
        setError("Please enter a valid email address");
        setLoading(false);
        return;
      }

      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/recovery-request/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          email: normalizedEmail,
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data?.detail || data?.message || "Something went wrong");
        return;
      }

      setSuccess(true);
      localStorage.setItem("pendingEmail", normalizedEmail);
      localStorage.setItem("recovery_request_time", new Date().toISOString());
      localStorage.setItem("recovery_user_timezone", userTimeZone);
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_requested", {
          email_domain: normalizedEmail.split('@')[1],
        });
      }

      setTimeout(() => navigate("/verification-password"), 1500);
    } catch (err) {
      console.error("Password recovery error:", err);
      setError("Network error. Please try again.");
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (error) setError("");
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && email.trim() && !loading) {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    
    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [email, loading]);

  return (
    <>
      <Helmet>
        <title>Reset Password | Halper AI</title>
        <meta name="description" content="Forgot your Halper AI password? Enter your email to receive a reset link and regain access to your account." />
      </Helmet>
    <div className="forgot-password">
      <main className="forgot-password-main">
        <div className="forgot-password-card">
          <div className="forgot-password-avatar">
            <div className="forgot-password-avatar-circle">
              <img src={register_mark} alt="reset" />
            </div>
          </div>

          <h2 className="forgot-password-title">Forgot Password</h2>
          <p className="forgot-password-subtitle">
            Please enter your email address to send you a code for recovering your account.
          </p>

          <form onSubmit={handleSubmit} className="forgot-password-form">
            <label className="forgot-password-label">
              Email Address <span className="required">*</span>
            </label>
            <div className="forgot-password-input-wrapper">
              <Mail className="forgot-password-icon" />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                autoComplete="email"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.trim() && !loading) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>

            {error && <p className="error-text">{error}</p>}
            <button 
              type="submit" 
              className="forgot-password-btn" 
              disabled={loading || !email.trim()}
            >
              {loading ? "Sending..." : "Send Code"}
            </button>
          </form>
        </div>
      </main>
    </div>
    </>
  );
}